/**
 * applyShellLangPolicy — the shell root's gate in front of the WORKSPACE
 * language policy (shared `applyWorkspaceLangPolicy`).
 *
 * The workspace policy resolves `enforced > workspace language > brand >
 * English`. That chain belongs to a SIGNED-IN shell: it is boot step 7b's
 * chain, and 7b only runs with a session. A signed-out shell (the /login
 * bounce, a guest route) gets its language from the public resolution
 * instead (shared publicBootLang.js: `?lang=` → brand → browser → English),
 * stamped by bootShell before the auth shell mounts.
 *
 * The shell root ran the workspace policy once at mount on EVERY shell,
 * signed out included. With no session there is no workspace, so it
 * answered English and overwrote the public language: a Georgian browser on
 * dev.my.symbols.app/login painted `ka`, then English (QA round 4, giorgi).
 *
 * Signed out → nothing (the public language stays). Signed in → the policy,
 * unchanged. An in-place sign-in reaches the signed-in branch when the full
 * workspace record lands (`onActiveWorkspaceFull` re-runs the handler).
 *
 * THE LANGUAGE AN IN-PAGE SIGN-IN OWES (KA-1 race; concierge headless check,
 * dev, en-US browser, Georgian workspace, 2026-10-30). That sign-in continues
 * in the same shell: the signed-in chrome mounts in the PUBLIC language, and
 * the workspace language arrives with the full record as a switch that loads
 * the Georgian bundles. The loader waited for the data load only: it lifted
 * at 3.0–3.3 s, and the switch landed at 2.7 s in one run and at 4.9 s in
 * another (English chrome on screen for ~1.6 s). `markShellLangOwed` below
 * raises `_langOwed` at that sign-in; the shell loader holds while it is up
 * and shows no word (app.js `_isLoaderActive`, MainLoader). The newest run of
 * this policy that DECIDES pays it, once its switch has applied.
 *
 * A `function`, not an arrow: `el.call` binds `this` to the element.
 *
 * @returns {Promise<boolean>|boolean} true when the session language changed
 */
export function applyShellLangPolicy() {
  const el = this
  const root = el?.state?.root || el?.state || null
  if (!root || root.signedIn !== true) return false
  const result = el.call('applyWorkspaceLangPolicy')
  // A run DECIDES on a record that is complete, or never will be. A
  // placeholder decides nothing: the run on the full record pays. Only the
  // newest deciding run pays: an older one that a newer switch superseded
  // resolves first, while the newer bundles still load.
  const ws = root.workspace
  if (
    root._langOwed === true &&
    !!ws &&
    !!(ws.id || ws._id) &&
    el.call('workspaceSettingsPending', root) !== true
  ) {
    const run = {}
    el._langOwedRun = run
    const pay = () => {
      if (el._langOwedRun !== run) return
      el._langOwedRun = null
      clearTimeout(el._langOwedCap)
      el._langOwedCap = null
      try {
        if (root._langOwed === true) root.update({ _langOwed: false })
      } catch (_) {
        /* torn down before the switch applied */
      }
    }
    Promise.resolve(result).then(pay, pay)
  }
  return result
}

/**
 * markShellLangOwed — a sign-in on this page owes the session its interface
 * language. The shell root's session subscription (app.js) calls it on each
 * new session, before the data load raises the loader, so the loader's first
 * frame already shows no word.
 *
 * Only a shell whose boot answered SIGNED OUT owes it: a signed-in boot
 * applied the session language before its mount (bootResolve step 7b, or the
 * warm snapshot's cached pair). The warm early mount's context has no verdict
 * yet at its first session tick (`bootSignedIn` undefined): the check is
 * strict.
 *
 * The cap pays a debt that no run decides (a record that never completes, an
 * account with no workspace) after the post-auth reveal's budget, 7 s.
 *
 * A `function`, not an arrow: `el.call` binds `this` to the element.
 *
 * @returns {boolean} true when the debt was raised
 */
export function markShellLangOwed() {
  const el = this
  const root = el?.state?.root || el?.state || null
  if (!root || typeof root.update !== 'function') return false
  if (el.context?.bootSignedIn !== false) return false
  clearTimeout(el._langOwedCap)
  el._langOwedRun = null
  root.update({ _langOwed: true })
  const cap = setTimeout(() => {
    if (el._langOwedCap !== cap) return
    el._langOwedCap = null
    el._langOwedRun = null
    try {
      if (root._langOwed === true) root.update({ _langOwed: false })
    } catch (_) {
      /* torn down */
    }
  }, 7000)
  el._langOwedCap = cap
  // The full record can land before this tick: its policy run is over, and it
  // ran with nothing owed. Run the policy once more; it pays when it decides.
  // No record, or a placeholder: the full record's own run comes later.
  const ws = root.workspace
  if (!!ws && !!(ws.id || ws._id) && el.call('workspaceSettingsPending', root) !== true) {
    Promise.resolve(el.call('applyShellLangPolicy')).catch((err) =>
      console.warn('[shell] workspace language policy failed:', err?.message || err)
    )
  }
  return true
}
